import { useQuery } from "@tanstack/react-query";
import { AlertCircle, ArrowLeftRight, ChevronRight, RefreshCw, Search, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { OnboardingDrawer } from "../components/OnboardingDrawer.js";
import { AppLayout } from "../components/layout/AppLayout.js";
import { Header } from "../components/layout/Header.js";
import { ProbezeitListe } from "../components/probezeit/ProbezeitListe.js";
import { KpiCard } from "../components/ui/KpiCard.js";
import { RiskBadge } from "../components/ui/RiskBadge.js";
import { useAuth } from "../hooks/useAuth.js";
import { type EmployeeRecord, employeesApi } from "../lib/api.js";
import { anteilProzent, mittelwertGerundet, trendDifferenz } from "../lib/onboarding-kennzahlen.js";
import { siehtHrAuswertung, wenigePersonenHinweis } from "../lib/probezeit-anzeige.js";

// ── Types ────────────────────────────────────────────────────────────────────

type Granularity = "month" | "quarter" | "year";
type SortKey =
  | "displayName"
  | "department"
  | "position"
  | "employmentCategory"
  | "hireDate"
  | "probation"
  | "tenureMonths"
  | "riskLevel"
  | "eventAttendanceRate"
  | "sickDays12m";
type SortDir = "asc" | "desc";
type CohortSegment =
  | "probationPassed"
  | "probationFailed"
  | "twelveMonthRetained"
  | "twelveMonthLost";
type CohortViewMode = "rate" | "absolute";

const SEGMENT_LABELS: Record<CohortSegment, string> = {
  probationPassed: "Probezeit bestanden",
  probationFailed: "Probezeit nicht bestanden",
  twelveMonthRetained: "Nach 12M geblieben",
  twelveMonthLost: "Innerhalb 12M ausgeschieden",
};
const SEGMENT_COLORS: Record<CohortSegment, string> = {
  probationPassed: "var(--risk-low)",
  probationFailed: "var(--risk-high)",
  twelveMonthRetained: "var(--risk-low)",
  twelveMonthLost: "var(--risk-high)",
};

// ── Date range helpers ──────────────────────────────────────────────────────

function toISODate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function defaultFrom(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 2);
  return toISODate(d);
}

function shiftRange(from: string, to: string, direction: -1 | 1): { from: string; to: string } {
  const f = new Date(from);
  const t = new Date(to);
  const spanMs = t.getTime() - f.getTime();
  if (direction === -1) {
    const newTo = new Date(f.getTime() - 1);
    const newFrom = new Date(newTo.getTime() - spanMs);
    return { from: toISODate(newFrom), to: toISODate(newTo) };
  }
  const newFrom = new Date(t.getTime() + 86400000);
  const newTo = new Date(newFrom.getTime() + spanMs);
  return { from: toISODate(newFrom), to: toISODate(newTo) };
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function tenureMonthsAt(hireDate: string, atDate: Date): number {
  return Math.floor((atDate.getTime() - new Date(hireDate).getTime()) / (1000 * 60 * 60 * 24 * 30));
}

function probationStatus(emp: EmployeeRecord): "passed" | "pending" | "failed" {
  if (!emp.hireDate) return "pending";
  const now = new Date();
  const probEnd = emp.probationPeriodEnd ? new Date(emp.probationPeriodEnd) : null;
  const sixMonthsAfterHire = new Date(
    new Date(emp.hireDate).getTime() + 6 * 30 * 24 * 60 * 60 * 1000,
  );
  const effectiveEnd = probEnd ?? sixMonthsAfterHire;
  if (emp.terminationDate && new Date(emp.terminationDate) <= effectiveEnd) return "failed";
  if (emp.status === "inactive" && emp.terminationDate) {
    return new Date(emp.terminationDate) > effectiveEnd ? "passed" : "failed";
  }
  if (now >= effectiveEnd && emp.status !== "inactive") return "passed";
  return "pending";
}

function exitPhase(emp: EmployeeRecord): "probation" | "post_probation" | "after_year" | null {
  if (!emp.terminationDate || !emp.hireDate) return null;
  const months = tenureMonthsAt(emp.hireDate, new Date(emp.terminationDate));
  if (months < 6) return "probation";
  if (months < 12) return "post_probation";
  return "after_year";
}

function cohortKey(hireDate: string, granularity: "month" | "quarter" | "year"): string {
  const d = new Date(hireDate);
  const y = d.getFullYear();
  const m = d.getMonth();
  if (granularity === "year") return `${y}`;
  if (granularity === "quarter") return `${y}-Q${Math.floor(m / 3) + 1}`;
  return `${y}-${String(m + 1).padStart(2, "0")}`;
}

const CATEGORY_LABELS: Record<string, string> = {
  consulting_tnw: "Consulting TNW",
  consulting_senior: "Consulting Senior",
  internal: "Internal",
  unknown: "Unbekannt",
};

const LEVEL_OPTIONS = [
  "All",
  "Trainee",
  "Professional Consultant",
  "Senior Consultant",
  "Expert Consultant",
] as const;

// Consulting Senior: nur echte Neueinstellungen auf Senior-Level, keine internen Beförderungen
const SENIOR_ENTRY_POSITIONS = new Set([
  "Senior Consultant",
  "Managing Consultant",
  "Manager",
  "Senior Manager",
  "Bereichsleiter",
]);

const PROBATION_LABELS: Record<string, { label: string; color: string }> = {
  passed: { label: "Bestanden", color: "var(--risk-low)" },
  pending: { label: "Laufend", color: "var(--accent-gold)" },
  failed: { label: "Nicht bestanden", color: "var(--risk-high)" },
};

// ── Main Component ───────────────────────────────────────────────────────────

export function OnboardingSuccess() {
  const { t } = useTranslation();

  // HR sehen die ganze Auswertung. Die Rolle kommt aus /me: Mit „Ansicht als“ meldet es die Rolle
  // der Führungskraft, und die Seite zeigt deren Sicht (Spezifikation R7, wie KpiReporting.tsx).
  // Welche Rollen HR sind, steht als Regel in `siehtHrAuswertung`.
  const { user } = useAuth();
  const istHr = siehtHrAuswertung(user?.role);

  // Time range state
  const [dateFrom, setDateFrom] = useState(defaultFrom);
  const [dateTo, setDateTo] = useState(() => toISODate(new Date()));
  const [compareEnabled, setCompareEnabled] = useState(false);

  // Cohort chart granularity (lives inside the chart now)
  const [granularity, setGranularity] = useState<Granularity>("quarter");

  // Filter state
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [levelFilter, setLevelFilter] = useState("All");
  const [reasonFilter, setReasonFilter] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  // Table state
  const [sortKey, setSortKey] = useState<SortKey>("hireDate");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);

  // Cohort drill-down state
  const [cohortDrilldown, setCohortDrilldown] = useState<{
    cohort: string;
    segment: CohortSegment;
  } | null>(null);

  // Data fetching — boardContext "onboarding" aktiviert 12-Monats-Historie
  // auf Ex-Mitarbeiter (für Manager, siehe scope.ts).
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["employees-with-inactive", "onboarding"],
    queryFn: () =>
      employeesApi.getAllForBoard({ includeInactive: true, boardContext: "onboarding" }),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const allEmployees = data?.employees ?? [];

  // ── Comparison period (same length, shifted back) ──────────────────────
  const comparePeriod = useMemo(() => {
    return shiftRange(dateFrom, dateTo, -1);
  }, [dateFrom, dateTo]);

  // ── Filter by date range + category/level/reason ──────────────────────
  function filterByRange(emps: EmployeeRecord[], from: string, to: string) {
    return emps.filter((emp) => {
      if (!emp.hireDate) return false;
      // Vergleich nur auf Datumsteil (YYYY-MM-DD), um Timezone-Probleme zu vermeiden
      const hireDay = emp.hireDate.slice(0, 10);
      if (hireDay < from || hireDay > to) return false;
      if (categoryFilter !== "All" && emp.employmentCategory !== categoryFilter) return false;
      // Consulting Senior: nur Neueinstellungen auf Senior-Level, keine internen Beförderungen
      if (categoryFilter === "consulting_senior" && !SENIOR_ENTRY_POSITIONS.has(emp.position ?? ""))
        return false;
      if (levelFilter !== "All" && emp.position !== levelFilter) return false;
      if (reasonFilter && emp.terminationReason !== reasonFilter) return false;
      return true;
    });
  }

  const filtered = useMemo(
    () => filterByRange(allEmployees, dateFrom, dateTo),
    [allEmployees, dateFrom, dateTo, categoryFilter, levelFilter, reasonFilter],
  );

  const compareFiltered = useMemo(
    () => (compareEnabled ? filterByRange(allEmployees, comparePeriod.from, comparePeriod.to) : []),
    [allEmployees, compareEnabled, comparePeriod, categoryFilter, levelFilter, reasonFilter],
  );

  // ── Onboarding-relevant employees (hired <12m ago or exited <12m tenure) ──
  const onboardingSet = useMemo(() => {
    const now = new Date();
    return filtered.filter((emp) => {
      if (!emp.hireDate) return false;
      if (emp.status === "inactive") return false;
      if (probationStatus(emp) === "failed") return false;
      const tenure = tenureMonthsAt(emp.hireDate, now);
      return tenure < 12;
    });
  }, [filtered]);

  // ── KPI calculation (reusable for both periods) ───────────────────────
  // Gerechnet wird mit den Regeln aus lib/onboarding-kennzahlen.ts. Steht hinter einer Kennzahl niemand in
  // der Stichprobe (Nenner 0), ist sie null und nicht 0; die Kachel zeigt dann den Strich. „Neue
  // Mitarbeiter“ (totalHiresN) ist eine Anzahl und bleibt eine Zahl.
  function calcKpis(emps: EmployeeRecord[]) {
    const now = new Date();
    const probationEligible = emps.filter((emp) => {
      if (!emp.hireDate) return false;
      const ps = probationStatus(emp);
      return ps === "passed" || ps === "failed";
    });
    const probationPassed = probationEligible.filter((emp) => probationStatus(emp) === "passed");
    const probationRate = anteilProzent(probationPassed.length, probationEligible.length);

    const twelveMonthEligible = emps.filter((emp) => {
      if (!emp.hireDate) return false;
      return tenureMonthsAt(emp.hireDate, now) >= 12;
    });
    const twelveMonthStayed = twelveMonthEligible.filter((emp) => {
      if (emp.status !== "inactive") return true;
      if (!emp.terminationDate || !emp.hireDate) return false;
      return tenureMonthsAt(emp.hireDate, new Date(emp.terminationDate)) >= 12;
    });
    const twelveMonthRate = anteilProzent(twelveMonthStayed.length, twelveMonthEligible.length);

    const newHires = emps.filter((emp) => emp.status !== "inactive").length;

    const earlyExits = emps.filter((emp) => {
      if (!emp.hireDate || !emp.terminationDate || emp.status !== "inactive") return false;
      return tenureMonthsAt(emp.hireDate, new Date(emp.terminationDate)) < 12;
    });
    const totalHires = emps.filter((emp) => emp.hireDate).length;
    const earlyTurnoverRate = anteilProzent(earlyExits.length, totalHires);

    const earlyExitTenures = earlyExits
      .filter((emp) => emp.hireDate && emp.terminationDate)
      .map((emp) => tenureMonthsAt(emp.hireDate!, new Date(emp.terminationDate!)));
    const avgTenureAtExit = mittelwertGerundet(earlyExitTenures);

    return {
      probationRate,
      twelveMonthRate,
      newHires,
      earlyTurnoverRate,
      avgTenureAtExit,
      // Datenbasis (Zähler/Nenner) zur Einordnung — gerade bei kleinen Mengen wichtig
      probationPassedN: probationPassed.length,
      probationEligibleN: probationEligible.length,
      twelveMonthStayedN: twelveMonthStayed.length,
      twelveMonthEligibleN: twelveMonthEligible.length,
      earlyExitsN: earlyExits.length,
      totalHiresN: totalHires,
      earlyExitTenuresN: earlyExitTenures.length,
    };
  }

  const kpis = useMemo(() => calcKpis(filtered), [filtered]);
  const compareKpis = useMemo(
    () => (compareEnabled ? calcKpis(compareFiltered) : null),
    [compareEnabled, compareFiltered],
  );

  // ── Termination reasons by phase ───────────────────────────────────────────
  const reasonsByPhase = useMemo(() => {
    const phases = ["probation", "post_probation", "after_year"] as const;
    const result: Record<string, Array<{ reason: string; count: number }>> = {};
    for (const phase of phases) {
      const emps = filtered.filter((emp) => exitPhase(emp) === phase && emp.terminationReason);
      const counts: Record<string, number> = {};
      for (const emp of emps) {
        const reason = emp.terminationReason!;
        counts[reason] = (counts[reason] || 0) + 1;
      }
      result[phase] = Object.entries(counts)
        .map(([reason, count]) => ({ reason, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 3);
    }
    return result;
  }, [filtered]);

  // ── Cohort chart data (survival rates) ─────────────────────────────────────
  const cohortData = useMemo(() => {
    const now = new Date();
    const cohorts: Record<string, { hires: EmployeeRecord[] }> = {};

    for (const emp of filtered) {
      if (!emp.hireDate) continue;
      const key = cohortKey(emp.hireDate, granularity);
      if (!cohorts[key]) cohorts[key] = { hires: [] };
      cohorts[key].hires.push(emp);
    }

    return Object.entries(cohorts)
      .map(([cohort, { hires }]) => {
        // Probation rate: only count employees whose probation is decided
        const probationDecided = hires.filter((e) => {
          const ps = probationStatus(e);
          return ps === "passed" || ps === "failed";
        });
        const probationPassedCount = probationDecided.filter(
          (e) => probationStatus(e) === "passed",
        ).length;
        const probationRate =
          probationDecided.length > 0
            ? Math.round((probationPassedCount / probationDecided.length) * 100)
            : null;

        // 12-month rate: only for cohorts old enough (≥12 months since cohort start)
        // Use the oldest hire date in the cohort to determine eligibility
        const oldestHire = hires.reduce(
          (oldest, e) =>
            !oldest || new Date(e.hireDate!) < new Date(oldest.hireDate!) ? e : oldest,
          null as EmployeeRecord | null,
        );
        const cohortAgeMonths = oldestHire ? tenureMonthsAt(oldestHire.hireDate!, now) : 0;
        const twelveMonthEligible = cohortAgeMonths >= 12;

        let twelveMonthRate: number | null = null;
        if (twelveMonthEligible) {
          const eligible = hires.filter((e) => e.hireDate && tenureMonthsAt(e.hireDate, now) >= 12);
          const retained = eligible.filter((e) => {
            if (e.status !== "inactive") return true;
            if (!e.terminationDate || !e.hireDate) return false;
            return tenureMonthsAt(e.hireDate, new Date(e.terminationDate)) >= 12;
          });
          twelveMonthRate =
            eligible.length > 0 ? Math.round((retained.length / eligible.length) * 100) : null;
        }

        return {
          cohort,
          probationRate,
          twelveMonthRate,
          total: hires.length,
          probationDecided: probationDecided.length,
        };
      })
      .sort((a, b) => a.cohort.localeCompare(b.cohort));
  }, [filtered, granularity]);

  // ── Cohort → segment → employees mapping (for drill-down) ────────────
  const cohortEmployeeMap = useMemo(() => {
    const now = new Date();
    const map: Record<string, Record<CohortSegment, EmployeeRecord[]>> = {};
    for (const emp of filtered) {
      if (!emp.hireDate) continue;
      const key = cohortKey(emp.hireDate, granularity);
      if (!map[key])
        map[key] = {
          probationPassed: [],
          probationFailed: [],
          twelveMonthRetained: [],
          twelveMonthLost: [],
        };

      const ps = probationStatus(emp);
      if (ps === "passed") map[key].probationPassed.push(emp);
      else if (ps === "failed") map[key].probationFailed.push(emp);

      // 12-month classification (only for employees with enough tenure)
      if (tenureMonthsAt(emp.hireDate, now) >= 12) {
        if (emp.status !== "inactive") {
          map[key].twelveMonthRetained.push(emp);
        } else if (emp.terminationDate) {
          if (tenureMonthsAt(emp.hireDate, new Date(emp.terminationDate)) >= 12) {
            map[key].twelveMonthRetained.push(emp);
          } else {
            map[key].twelveMonthLost.push(emp);
          }
        }
      }
    }
    return map;
  }, [filtered, granularity]);

  const drilldownEmployees = useMemo(() => {
    if (!cohortDrilldown) return [];
    return cohortEmployeeMap[cohortDrilldown.cohort]?.[cohortDrilldown.segment] ?? [];
  }, [cohortDrilldown, cohortEmployeeMap]);

  // ── Heatmap data ───────────────────────────────────────────────────────────
  const heatmapByCategory = useMemo(() => {
    const categories = ["consulting_tnw", "consulting_senior", "internal"];
    return categories.map((cat) => {
      const emps = filtered.filter((emp) => emp.employmentCategory === cat && emp.hireDate);
      const now = new Date();
      const eligible = emps.filter(
        (emp) => tenureMonthsAt(emp.hireDate!, now) >= 12 || emp.status === "inactive",
      );
      const retained = eligible.filter((emp) => emp.status !== "inactive");
      const rate = eligible.length > 0 ? Math.round((retained.length / eligible.length) * 100) : -1;
      return { label: CATEGORY_LABELS[cat] ?? cat, rate, total: eligible.length };
    });
  }, [filtered]);

  const heatmapByLevel = useMemo(() => {
    const levels = ["Trainee", "Professional Consultant", "Senior Consultant", "Expert Consultant"];
    return levels.map((level) => {
      const emps = filtered.filter((emp) => emp.position === level && emp.hireDate);
      const now = new Date();
      const eligible = emps.filter(
        (emp) => tenureMonthsAt(emp.hireDate!, now) >= 12 || emp.status === "inactive",
      );
      const retained = eligible.filter((emp) => emp.status !== "inactive");
      const rate = eligible.length > 0 ? Math.round((retained.length / eligible.length) * 100) : -1;
      return { label: level, rate, total: eligible.length };
    });
  }, [filtered]);

  // ── Employee table data ────────────────────────────────────────────────────
  const tableData = useMemo(() => {
    const searchLower = search.toLowerCase();
    let rows = onboardingSet.filter((emp) => {
      if (!search) return true;
      return (
        emp.displayName.toLowerCase().includes(searchLower) ||
        (emp.department ?? "").toLowerCase().includes(searchLower) ||
        (emp.position ?? "").toLowerCase().includes(searchLower)
      );
    });

    rows = [...rows].sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      switch (sortKey) {
        case "displayName":
          return dir * a.displayName.localeCompare(b.displayName);
        case "department":
          return dir * (a.department ?? "").localeCompare(b.department ?? "");
        case "position":
          return dir * (a.position ?? "").localeCompare(b.position ?? "");
        case "employmentCategory":
          return dir * a.employmentCategory.localeCompare(b.employmentCategory);
        case "hireDate":
          return dir * (a.hireDate ?? "").localeCompare(b.hireDate ?? "");
        case "probation": {
          const order = { passed: 0, pending: 1, failed: 2 };
          return dir * (order[probationStatus(a)] - order[probationStatus(b)]);
        }
        case "tenureMonths":
          return dir * ((a.tenureMonths ?? 0) - (b.tenureMonths ?? 0));
        case "riskLevel": {
          const riskOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
          return (
            dir * ((riskOrder[a.riskLevel ?? "low"] ?? 3) - (riskOrder[b.riskLevel ?? "low"] ?? 3))
          );
        }
        case "eventAttendanceRate":
          return dir * ((a.eventAttendanceRate ?? 0) - (b.eventAttendanceRate ?? 0));
        case "sickDays12m":
          return dir * ((a.sickDays12m ?? 0) - (b.sickDays12m ?? 0));
        default:
          return 0;
      }
    });
    return rows;
  }, [onboardingSet, search, sortKey, sortDir]);

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  // Laden und Fehler der Abfrage betreffen nur den Teil unter der Liste „In der Probezeit“ (siehe
  // unten): Es gibt hier keinen frühen `return`, der die Seite im ganzen ersetzte.
  return (
    <AppLayout>
      <Header title={t("onboarding.title")} subtitle={t("onboarding.subtitle")} />

      <div className="flex-1 overflow-y-auto flex flex-col gap-6 p-6">
        {/* ── 0. In der Probezeit (Spezifikation R7): für alle Rollen, ganz oben ───────
            Steht in jedem Zustand der Seite, auch solange die Abfrage der Seite
            (`employees-with-inactive`) lädt oder scheitert: Die Liste hat ihre eigene Abfrage.
            Stünde sie hinter dem Laden und dem Fehler jener Abfrage, begänne ihre Anfrage erst
            danach (die Wartezeiten addierten sich), und scheiterte jene, verlöre man diesen
            Abschnitt. Dasselbe gilt für das Fenster am Ende der Seite.
            Die Überschrift gehört zu dem, was darunter steht: -mb-3 zieht sie näher an den
            Inhalt als an den Abschnitt davor (die Spalte hat gap-6). Alle drei Überschriften der
            Seite machen es so. */}
        <h2 className="text-base font-semibold -mb-3" style={{ color: "var(--text-primary)" }}>
          In der Probezeit
        </h2>
        <ProbezeitListe onPerson={(id) => setSelectedEmployeeId(id)} />

        {/* ── Der Teil unter der Liste wechselt mit der Abfrage der Seite ──────────────── */}
        {isLoading && (
          <div
            className="flex items-center justify-center gap-2 py-20"
            style={{ color: "var(--text-muted)" }}
          >
            <RefreshCw size={16} className="animate-spin" />
            <span className="text-sm">Lade Onboarding-Daten…</span>
          </div>
        )}

        {isError && (
          <div
            className="flex flex-col items-center gap-3 py-20"
            style={{ color: "var(--text-muted)" }}
          >
            <AlertCircle size={24} />
            <p className="text-sm">Fehler beim Laden der Daten</p>
            <button
              type="button"
              className="text-xs px-3 py-1.5 rounded"
              style={{ background: "var(--accent)", color: "#fff" }}
              onClick={() => refetch()}
            >
              Erneut versuchen
            </button>
          </div>
        )}

        {!isLoading && !isError && (
          <>
            <h2
              className="text-base font-semibold mt-4 -mb-3"
              style={{ color: "var(--text-primary)" }}
            >
              Wie gelingt der Einstieg?
            </h2>

            {/* ── 1. Time Range & Filters ─────────────────────────────────── */}
            <div
              className="flex items-center gap-3 flex-wrap p-4 rounded-lg"
              style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
            >
              {/* Date range */}
              <div className="flex items-center gap-2">
                <label
                  className="text-[10px] font-semibold uppercase tracking-wider"
                  style={{ color: "var(--text-muted)" }}
                >
                  Zeitraum
                </label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="px-2 py-1.5 text-xs rounded"
                  style={{
                    background: "var(--bg-surface)",
                    color: "var(--text-primary)",
                    border: "1px solid var(--border)",
                  }}
                />
                <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                  –
                </span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="px-2 py-1.5 text-xs rounded"
                  style={{
                    background: "var(--bg-surface)",
                    color: "var(--text-primary)",
                    border: "1px solid var(--border)",
                  }}
                />
              </div>

              {/* Compare toggle */}
              <button
                type="button"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded font-medium transition-colors"
                style={{
                  background: compareEnabled ? "var(--accent)" : "var(--bg-surface)",
                  color: compareEnabled ? "#fff" : "var(--text-secondary)",
                  border: compareEnabled ? "1px solid var(--accent)" : "1px solid var(--border)",
                }}
                onClick={() => setCompareEnabled(!compareEnabled)}
              >
                <ArrowLeftRight size={12} />
                Vergleich
              </button>

              {compareEnabled && (
                <span
                  className="text-[10px] px-2 py-1 rounded"
                  style={{ background: "var(--bg-hover)", color: "var(--text-muted)" }}
                >
                  vs. {comparePeriod.from} – {comparePeriod.to}
                </span>
              )}

              <div style={{ width: 1, height: 24, background: "var(--border)" }} />

              {/* Category dropdown */}
              <select
                className="px-3 py-1.5 text-xs rounded"
                style={{
                  background: "var(--bg-surface)",
                  color: "var(--text-primary)",
                  border: "1px solid var(--border)",
                }}
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="All">Alle Kategorien</option>
                <option value="consulting_tnw">Consulting TNW</option>
                <option value="consulting_senior">Consulting Senior</option>
                <option value="internal">Internal</option>
              </select>

              {/* Level dropdown */}
              <select
                className="px-3 py-1.5 text-xs rounded"
                style={{
                  background: "var(--bg-surface)",
                  color: "var(--text-primary)",
                  border: "1px solid var(--border)",
                }}
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
              >
                {LEVEL_OPTIONS.map((l) => (
                  <option key={l} value={l}>
                    {l === "All" ? "Alle Level" : l}
                  </option>
                ))}
              </select>

              {/* Reason filter chip */}
              {reasonFilter && (
                <span
                  className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full"
                  style={{
                    background: "rgba(234,91,11,0.12)",
                    color: "var(--accent)",
                    border: "1px solid rgba(234,91,11,0.3)",
                  }}
                >
                  {reasonFilter}
                  <X size={12} className="cursor-pointer" onClick={() => setReasonFilter(null)} />
                </span>
              )}
            </div>

            {data?.historyStatus === "unavailable" && (
              <div
                className="rounded-lg p-3 text-xs"
                style={{
                  background: "var(--bg-card)",
                  border: "1px solid var(--border)",
                  color: "var(--text-muted)",
                }}
              >
                Historische Berichtslinien-Daten sind nicht verfügbar — ausgeschiedene Mitarbeiter
                werden deshalb nicht angezeigt. (Org-Historie-Sync prüfen.)
              </div>
            )}

            {/* ── 2. KPI Cards ─────────────────────────────────────────────────
                Der Hinweis „beruht auf N Personen“ (Spezifikation R7, Punkt 2) steht an den drei
                Quoten, je mit deren eigenem Nenner, der Zahl hinter „von“ (Plan A2, Task 6, Nachtrag 3
                Punkt 13). „Neue Mitarbeiter“ ist eine Anzahl und „Ø Betriebszugehörigkeit bei Austritt“
                ein Mittelwert, beide ohne Hinweis.
                Steht hinter einer Kachel niemand in der Stichprobe („0 von 0“, „Ø aus 0 Austritten“), hat sie
                keinen Wert, und die Kachel zeigt den Strich statt „0 %“ (Entscheidung vom 14.10.2026, Regeln
                in lib/onboarding-kennzahlen.ts). Das gilt für die drei Quoten und den Mittelwert. „Neue
                Mitarbeiter“ bleibt eine Zahl: Null Einstellungen sind eine 0. */}
            <div className="grid grid-cols-5 gap-4">
              <KpiCard
                label={t("onboarding.kpi.probationRetention")}
                value={kpis.probationRate}
                unit="%"
                trend={trendDifferenz(kpis.probationRate, compareKpis?.probationRate)}
                basis={`${kpis.probationPassedN} von ${kpis.probationEligibleN}`}
                hinweis={wenigePersonenHinweis(kpis.probationEligibleN)}
                info="Anteil der Mitarbeiter, die ihre Probezeit erfolgreich abgeschlossen haben. Berechnung: Probezeit bestanden / (Probezeit bestanden + nicht bestanden). Nur Mitarbeiter mit abgelaufener Probezeit fließen ein."
              />
              <KpiCard
                label={t("onboarding.kpi.yearRetention")}
                value={kpis.twelveMonthRate}
                unit="%"
                trend={trendDifferenz(kpis.twelveMonthRate, compareKpis?.twelveMonthRate)}
                basis={`${kpis.twelveMonthStayedN} von ${kpis.twelveMonthEligibleN}`}
                hinweis={wenigePersonenHinweis(kpis.twelveMonthEligibleN)}
                info="Anteil der Einstellungen, die mindestens 12 Monate im Unternehmen geblieben sind. Berechnung: (Noch aktive MA + MA die nach ≥12 Monaten ausgeschieden sind) / Alle Einstellungen mit ≥12 Monaten seit Eintritt. MA die nach z.B. 18 Monaten gegangen sind zählen als 'geblieben'."
              />
              <KpiCard
                label={t("onboarding.kpi.newHires")}
                value={kpis.totalHiresN}
                unit=""
                trend={compareKpis ? kpis.totalHiresN - compareKpis.totalHiresN : 0}
                basis={`${kpis.newHires} aktiv · ${kpis.totalHiresN - kpis.newHires} ausgeschieden`}
                info="Alle Mitarbeiter, die im gewählten Zeitraum eingestiegen sind (eingetreten = Eintrittsdatum im Zeitraum), inkl. der inzwischen Ausgeschiedenen. Aufschlüsselung: wie viele davon aktuell noch aktiv bzw. wieder ausgeschieden sind."
              />
              <KpiCard
                label={t("onboarding.kpi.earlyAttrition")}
                value={kpis.earlyTurnoverRate}
                unit="%"
                trend={trendDifferenz(kpis.earlyTurnoverRate, compareKpis?.earlyTurnoverRate)}
                inverse={true}
                basis={`${kpis.earlyExitsN} von ${kpis.totalHiresN}`}
                hinweis={wenigePersonenHinweis(kpis.totalHiresN)}
                info="Anteil der Einstellungen im Zeitraum, die innerhalb der ersten 12 Monate wieder ausgeschieden sind. Berechnung: Frühaustritte (<12 Monate Betriebszugehörigkeit) / alle Einstellungen. Ein hoher Wert deutet auf systematische Onboarding-Probleme hin."
              />
              <KpiCard
                label={t("onboarding.kpi.avgTenureAtExit")}
                value={kpis.avgTenureAtExit}
                unit="M"
                trend={trendDifferenz(kpis.avgTenureAtExit, compareKpis?.avgTenureAtExit)}
                basis={`Ø aus ${kpis.earlyExitTenuresN} Austritt${kpis.earlyExitTenuresN === 1 ? "" : "en"}`}
                info="Durchschnittliche Betriebszugehörigkeit (in Monaten) der Mitarbeiter, die innerhalb der ersten 12 Monate das Unternehmen verlassen haben. Zeigt, wie früh im Onboarding-Prozess Mitarbeiter typischerweise ausscheiden."
              />
            </div>

            {/* ── 3. Termination Reasons Panel ────────────────────────────────── */}
            <TerminationReasonsPanel
              reasonsByPhase={reasonsByPhase}
              onReasonClick={(reason) => setReasonFilter(reasonFilter === reason ? null : reason)}
              activeReason={reasonFilter}
            />

            {/* ── HR-Teil (Spezifikation R7): Kohorten, Heatmaps und Mitarbeiterliste des ersten
                Jahres sehen nur HR (admin, hrbp). Die Abschnitte 4 bis 6 sind inhaltlich
                unverändert. */}
            {istHr && (
              <>
                <h2
                  className="text-base font-semibold mt-4 -mb-3"
                  style={{ color: "var(--text-primary)" }}
                >
                  Auswertung für HR
                </h2>

                {/* ── 4. Cohort Comparison Chart ──────────────────────────────────── */}
                {cohortData.length > 0 && (
                  <CohortChart
                    data={cohortData}
                    title={t("onboarding.cohortChart")}
                    granularity={granularity}
                    onGranularityChange={(g) => {
                      setGranularity(g);
                      setCohortDrilldown(null);
                    }}
                    drilldown={cohortDrilldown}
                    drilldownEmployees={drilldownEmployees}
                    onBarClick={(cohort, segment) => {
                      if (
                        cohortDrilldown?.cohort === cohort &&
                        cohortDrilldown?.segment === segment
                      ) {
                        setCohortDrilldown(null);
                      } else {
                        setCohortDrilldown({ cohort, segment });
                      }
                    }}
                    onDrilldownClose={() => setCohortDrilldown(null)}
                    onEmployeeClick={(id) => setSelectedEmployeeId(id)}
                  />
                )}

                {/* ── 5. Heatmaps ────────────────────────────────────────────────── */}
                <div className="grid grid-cols-2 gap-4">
                  <HeatmapCard title={t("onboarding.byCategory")} data={heatmapByCategory} />
                  <HeatmapCard title={t("onboarding.byLevel")} data={heatmapByLevel} />
                </div>

                {/* ── 6. Employee Table ───────────────────────────────────────────── */}
                <EmployeeTable
                  data={tableData}
                  search={search}
                  onSearchChange={setSearch}
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={handleSort}
                  onRowClick={(id) => setSelectedEmployeeId(id)}
                />
              </>
            )}
          </>
        )}
      </div>

      {/* ── 7. Onboarding Drawer ───────────────────────────────────────
          Steht in jedem Zustand der Seite: Ein Klick auf eine Zeile der Liste öffnet es auch dann,
          wenn die Abfrage der Seite noch lädt oder gescheitert ist. */}
      {selectedEmployeeId !== null && (
        <OnboardingDrawer
          employeeId={selectedEmployeeId}
          onClose={() => setSelectedEmployeeId(null)}
        />
      )}
    </AppLayout>
  );
}

// ── Sub-Components ───────────────────────────────────────────────────────────

function TerminationReasonsPanel({
  reasonsByPhase,
  onReasonClick,
  activeReason,
}: {
  reasonsByPhase: Record<string, Array<{ reason: string; count: number }>>;
  onReasonClick: (reason: string) => void;
  activeReason: string | null;
}) {
  const { t } = useTranslation();
  const columns = [
    { key: "probation", label: t("onboarding.inProbation") },
    { key: "post_probation", label: t("onboarding.afterProbation") },
    { key: "after_year", label: t("onboarding.afterYear") },
  ];

  return (
    <div
      className="rounded-lg"
      style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
    >
      <div className="px-4 py-3" style={{ borderBottom: "1px solid var(--border)" }}>
        <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
          {t("onboarding.terminationReasons")}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-4 p-4">
        {columns.map(({ key, label }) => (
          <div key={key}>
            <p className="text-xs font-medium mb-3" style={{ color: "var(--text-muted)" }}>
              {label}
            </p>
            <div className="flex flex-col gap-2">
              {(reasonsByPhase[key] ?? []).length === 0 ? (
                <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                  Keine Daten
                </p>
              ) : (
                (reasonsByPhase[key] ?? []).map(({ reason, count }) => (
                  <button
                    type="button"
                    key={reason}
                    className="flex items-center justify-between text-xs px-3 py-2 rounded transition-colors text-left"
                    style={{
                      background:
                        activeReason === reason ? "rgba(234,91,11,0.12)" : "var(--bg-surface)",
                      color: activeReason === reason ? "var(--accent)" : "var(--text-primary)",
                      border:
                        activeReason === reason
                          ? "1px solid rgba(234,91,11,0.3)"
                          : "1px solid transparent",
                    }}
                    onClick={() => onReasonClick(reason)}
                  >
                    <span>{reason}</span>
                    <span
                      className="font-mono text-xs font-medium px-1.5 py-0.5 rounded"
                      style={{ background: "var(--bg-hover)", color: "var(--text-muted)" }}
                    >
                      {count}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface CohortDataRow {
  cohort: string;
  probationRate: number | null;
  twelveMonthRate: number | null;
  total: number;
  probationDecided: number;
}

function CohortChart({
  data,
  title,
  granularity,
  onGranularityChange,
  drilldown,
  drilldownEmployees,
  onBarClick,
  onDrilldownClose,
  onEmployeeClick,
}: {
  data: CohortDataRow[];
  title: string;
  granularity: Granularity;
  onGranularityChange: (g: Granularity) => void;
  drilldown: { cohort: string; segment: CohortSegment } | null;
  drilldownEmployees: EmployeeRecord[];
  onBarClick: (cohort: string, segment: CohortSegment) => void;
  onDrilldownClose: () => void;
  onEmployeeClick: (id: number) => void;
}) {
  const [viewMode, setViewMode] = useState<CohortViewMode>("rate");

  // For absolute view, transform data to show counts
  const chartData = useMemo(() => {
    if (viewMode === "rate") {
      return data.map((d) => ({
        cohort: d.cohort,
        "Probezeit-Rate": d.probationRate,
        "12M-Verbleibrate": d.twelveMonthRate,
      }));
    }
    return data.map((d) => ({
      cohort: d.cohort,
      Einstellungen: d.total,
      "Probezeit bestanden":
        d.probationRate !== null ? Math.round((d.probationRate / 100) * d.probationDecided) : 0,
    }));
  }, [data, viewMode]);

  const handleBarClick = (segment: CohortSegment) => (barData: Record<string, unknown>) => {
    const cohort = barData.cohort as string;
    if (cohort) onBarClick(cohort, segment);
  };

  return (
    <div
      className="rounded-lg"
      style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
    >
      <div
        className="px-4 py-3 flex items-center justify-between"
        style={{ borderBottom: "1px solid var(--border)" }}
      >
        <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
          {title}
        </span>
        <div className="flex items-center gap-2">
          {/* Rate / Absolut toggle */}
          <div
            className="flex rounded-md overflow-hidden"
            style={{ border: "1px solid var(--border)" }}
          >
            {(["rate", "absolute"] as CohortViewMode[]).map((m) => (
              <button
                type="button"
                key={m}
                className="px-3 py-1 text-xs font-medium transition-colors"
                style={{
                  background: viewMode === m ? "var(--accent)" : "transparent",
                  color: viewMode === m ? "#fff" : "var(--text-secondary)",
                }}
                onClick={() => setViewMode(m)}
              >
                {m === "rate" ? "Rate %" : "Absolut"}
              </button>
            ))}
          </div>
          {/* Granularity toggle */}
          <div
            className="flex rounded-md overflow-hidden"
            style={{ border: "1px solid var(--border)" }}
          >
            {(["month", "quarter", "year"] as Granularity[]).map((g) => (
              <button
                type="button"
                key={g}
                className="px-3 py-1 text-xs font-medium transition-colors"
                style={{
                  background: granularity === g ? "var(--accent)" : "transparent",
                  color: granularity === g ? "#fff" : "var(--text-secondary)",
                }}
                onClick={() => onGranularityChange(g)}
              >
                {g === "month" ? "Monat" : g === "quarter" ? "Quartal" : "Jahr"}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="p-4" style={{ height: 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          {viewMode === "rate" ? (
            <BarChart
              data={chartData}
              margin={{ top: 8, right: 16, bottom: 4, left: 0 }}
              style={{ cursor: "pointer" }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="cohort" tick={{ fontSize: 11, fill: "var(--text-muted)" }} />
              <YAxis
                domain={[0, 100]}
                tick={{ fontSize: 11, fill: "var(--text-muted)" }}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip
                contentStyle={{
                  background: "var(--bg-card)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  fontSize: 12,
                  color: "var(--text-primary)",
                }}
                formatter={(value: unknown) =>
                  value != null ? [`${value}%`, undefined] : ["–", undefined]
                }
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar
                dataKey="Probezeit-Rate"
                fill="var(--accent)"
                radius={[3, 3, 0, 0]}
                onClick={handleBarClick("probationPassed")}
                className="cursor-pointer"
              />
              <Bar
                dataKey="12M-Verbleibrate"
                fill="var(--risk-low)"
                radius={[3, 3, 0, 0]}
                onClick={handleBarClick("twelveMonthRetained")}
                className="cursor-pointer"
              />
            </BarChart>
          ) : (
            <BarChart
              data={chartData}
              margin={{ top: 8, right: 16, bottom: 4, left: 0 }}
              style={{ cursor: "pointer" }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="cohort" tick={{ fontSize: 11, fill: "var(--text-muted)" }} />
              <YAxis tick={{ fontSize: 11, fill: "var(--text-muted)" }} />
              <Tooltip
                contentStyle={{
                  background: "var(--bg-card)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  fontSize: 12,
                  color: "var(--text-primary)",
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar
                dataKey="Einstellungen"
                fill="var(--text-muted)"
                radius={[0, 0, 0, 0]}
                opacity={0.3}
              />
              <Bar
                dataKey="Probezeit bestanden"
                fill="var(--accent)"
                radius={[3, 3, 0, 0]}
                onClick={handleBarClick("probationPassed")}
                className="cursor-pointer"
              />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>

      {/* ── Cohort info line ──────────────────────────────────────────── */}
      <div className="px-4 pb-2">
        <p className="text-xs" style={{ color: "var(--text-muted)" }}>
          {viewMode === "rate"
            ? "Kohorten ohne ausreichende Reife zeigen keine 12M-Rate. Klick auf einen Balken zeigt die zugehörigen Mitarbeiter."
            : "Klick auf einen Balken zeigt die zugehörigen Mitarbeiter."}
        </p>
      </div>

      {/* ── Drill-down panel ─────────────────────────────────────────── */}
      {drilldown && (
        <div style={{ borderTop: "1px solid var(--border)" }}>
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span
                className="w-3 h-3 rounded-sm inline-block"
                style={{ background: SEGMENT_COLORS[drilldown.segment] }}
              />
              <span className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>
                {drilldown.cohort} — {SEGMENT_LABELS[drilldown.segment]}
              </span>
              <span
                className="text-xs px-1.5 py-0.5 rounded"
                style={{ background: "var(--bg-hover)", color: "var(--text-muted)" }}
              >
                {drilldownEmployees.length}
              </span>
            </div>
            <button
              type="button"
              className="p-1 rounded transition-colors"
              style={{ color: "var(--text-muted)" }}
              onClick={onDrilldownClose}
            >
              <X size={14} />
            </button>
          </div>
          <div
            className="px-4 pb-4 flex flex-col gap-1"
            style={{ maxHeight: 240, overflowY: "auto" }}
          >
            {drilldownEmployees.length === 0 ? (
              <p className="text-xs py-2" style={{ color: "var(--text-muted)" }}>
                Keine Mitarbeiter
              </p>
            ) : (
              drilldownEmployees.map((emp) => (
                <button
                  type="button"
                  key={emp.id}
                  className="flex items-center justify-between px-3 py-2 rounded text-left transition-colors"
                  style={{ background: "var(--bg-surface)", color: "var(--text-primary)" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "var(--bg-hover)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "var(--bg-surface)";
                  }}
                  onClick={() => onEmployeeClick(emp.id)}
                >
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs font-medium">{emp.displayName}</span>
                    <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                      {emp.position ?? "–"} · {emp.department ?? "–"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {emp.hireDate && (
                      <span className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>
                        {emp.hireDate}
                      </span>
                    )}
                    <ChevronRight size={14} style={{ color: "var(--text-muted)" }} />
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function HeatmapCard({
  title,
  data,
}: {
  title: string;
  data: Array<{ label: string; rate: number; total: number }>;
}) {
  function cellColor(rate: number): string {
    if (rate < 0) return "var(--bg-hover)";
    if (rate >= 90) return "var(--risk-low)";
    if (rate >= 70) return "var(--accent-gold)";
    return "var(--risk-high)";
  }

  return (
    <div
      className="rounded-lg"
      style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
    >
      <div className="px-4 py-3" style={{ borderBottom: "1px solid var(--border)" }}>
        <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
          {title}
        </span>
      </div>
      <div className="p-4 flex flex-col gap-2">
        {data.map(({ label, rate, total }) => (
          <div key={label} className="flex items-center gap-3">
            <span className="text-xs w-40 truncate" style={{ color: "var(--text-secondary)" }}>
              {label}
            </span>
            <div
              className="flex-1 h-8 rounded flex items-center justify-center"
              style={{
                background:
                  rate < 0
                    ? "var(--bg-hover)"
                    : `color-mix(in srgb, ${cellColor(rate)} 20%, transparent)`,
                border: `1px solid ${cellColor(rate)}`,
              }}
            >
              <span
                className="text-xs font-mono font-medium"
                style={{ color: rate < 0 ? "var(--text-muted)" : cellColor(rate) }}
              >
                {rate < 0 ? "n/a" : `${rate}%`}
              </span>
            </div>
            <span
              className="text-xs font-mono w-12 text-right"
              style={{ color: "var(--text-muted)" }}
            >
              n={total}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const TABLE_COLUMNS: Array<{ key: SortKey; label: string; width: string }> = [
  { key: "displayName", label: "Name", width: "1.5fr" },
  { key: "department", label: "Abteilung", width: "1fr" },
  { key: "position", label: "Einstiegslevel", width: "1fr" },
  { key: "employmentCategory", label: "Kategorie", width: "0.8fr" },
  { key: "hireDate", label: "Eintritt", width: "0.7fr" },
  { key: "probation", label: "Probezeit", width: "0.7fr" },
  { key: "tenureMonths", label: "Tenure", width: "0.5fr" },
  { key: "riskLevel", label: "Risiko", width: "0.6fr" },
  { key: "eventAttendanceRate", label: "Events", width: "0.5fr" },
  { key: "sickDays12m", label: "Eigene Krankheitstage", width: "0.7fr" },
];

function EmployeeTable({
  data,
  search,
  onSearchChange,
  sortKey,
  sortDir,
  onSort,
  onRowClick,
}: {
  data: EmployeeRecord[];
  search: string;
  onSearchChange: (v: string) => void;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
  onRowClick: (id: number) => void;
}) {
  const { t } = useTranslation();
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const gridTemplate = TABLE_COLUMNS.map((c) => c.width).join(" ");

  return (
    <div
      className="rounded-lg"
      style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3"
        style={{ borderBottom: "1px solid var(--border)" }}
      >
        <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
          {t("onboarding.employeeList")}
        </span>
        <div className="flex items-center gap-2">
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded"
            style={{ background: "var(--bg-surface)", border: "1px solid var(--border)" }}
          >
            <Search size={13} style={{ color: "var(--text-muted)" }} />
            <input
              type="text"
              placeholder="Suche…"
              className="bg-transparent text-xs outline-none"
              style={{ color: "var(--text-primary)", width: 140 }}
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>
            {data.length} Mitarbeiter
          </span>
        </div>
      </div>

      {/* Column headers */}
      <div
        className="grid px-4 py-2 text-xs font-medium"
        style={{
          gridTemplateColumns: gridTemplate,
          color: "var(--text-muted)",
          borderBottom: "1px solid var(--border)",
          background: "var(--bg-surface)",
        }}
      >
        {TABLE_COLUMNS.map((col) => (
          <button
            type="button"
            key={col.key}
            className="text-left flex items-center gap-1 hover:opacity-80"
            onClick={() => onSort(col.key)}
          >
            {col.label}
            {sortKey === col.key && (
              <span className="text-[10px]">{sortDir === "asc" ? "↑" : "↓"}</span>
            )}
          </button>
        ))}
      </div>

      {/* Rows */}
      <div style={{ maxHeight: 480, overflowY: "auto" }}>
        {data.length === 0 ? (
          <div className="py-8 text-center text-xs" style={{ color: "var(--text-muted)" }}>
            Keine Mitarbeiter gefunden
          </div>
        ) : (
          data.map((emp) => {
            const ps = probationStatus(emp);
            const psInfo = PROBATION_LABELS[ps];
            return (
              <div
                key={emp.id}
                className="grid px-4 py-2.5 text-xs cursor-pointer transition-colors"
                style={{
                  gridTemplateColumns: gridTemplate,
                  color: "var(--text-primary)",
                  borderBottom: "1px solid var(--border)",
                  background: hoveredRow === emp.id ? "var(--bg-hover)" : "transparent",
                }}
                onMouseEnter={() => setHoveredRow(emp.id)}
                onMouseLeave={() => setHoveredRow(null)}
                onClick={() => onRowClick(emp.id)}
              >
                <span className="truncate font-medium">{emp.displayName}</span>
                <span className="truncate" style={{ color: "var(--text-secondary)" }}>
                  {emp.department ?? "–"}
                </span>
                <span className="truncate" style={{ color: "var(--text-secondary)" }}>
                  {emp.position ?? "–"}
                </span>
                <span className="truncate" style={{ color: "var(--text-secondary)" }}>
                  {CATEGORY_LABELS[emp.employmentCategory] ?? emp.employmentCategory}
                </span>
                <span className="font-mono" style={{ color: "var(--text-secondary)" }}>
                  {emp.hireDate
                    ? new Date(emp.hireDate).toLocaleDateString("de-DE", {
                        year: "2-digit",
                        month: "2-digit",
                        day: "2-digit",
                      })
                    : "–"}
                </span>
                <span style={{ color: psInfo.color }}>{psInfo.label}</span>
                <span className="font-mono">{emp.tenureMonths ?? "–"}M</span>
                <span>{emp.riskLevel ? <RiskBadge level={emp.riskLevel} /> : "–"}</span>
                <span className="font-mono" style={{ color: "var(--text-secondary)" }}>
                  {emp.eventAttendanceRate != null
                    ? `${Math.round(emp.eventAttendanceRate * 100)}%`
                    : "–"}
                </span>
                <span className="font-mono" style={{ color: "var(--text-secondary)" }}>
                  {emp.sickDays12m != null ? emp.sickDays12m.toLocaleString("de-DE") : "–"}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
